import { useQuery, useQueryClient } from '@tanstack/solid-query'
import { For, Show } from 'solid-js'
import { toast } from 'solid-sonner'
import { trpc } from '../../trpc.ts'

/*
  the guest allow-list. an index checked here can be searched and read by anyone who
  can reach the server, no login - that's the whole point, and it's also why the page
  says so out loud rather than showing a bare list of checkboxes.

  with nothing checked there is no guest mode at all and anonymous visitors get the
  login page, exactly as before this existed
*/
export function GuestAccessPage() {
  const queryClient = useQueryClient()

  const reposQuery = useQuery(() => ({ queryKey: ['allRepos'], queryFn: () => trpc.search.allRepos.query() }))
  const guestQuery = useQuery(() => ({ queryKey: ['guestRepos'], queryFn: () => trpc.guest.list.query() }))

  const isPublic = (repo: string) => (guestQuery.data ?? []).some(it => it.repo === repo)

  // rows for indexes that no longer exist: a repo can be unmounted while its grant
  // stays behind, and leaving it invisible would make it a surprise when it returns
  const stale = () => (guestQuery.data ?? []).filter(it => !it.present)

  async function toggle(repo: string, enabled: boolean) {
    try {
      await trpc.guest.set.mutate({ repo, enabled })
      await queryClient.invalidateQueries({ queryKey: ['guestRepos'] })
      toast.success(enabled ? `${repo} is public` : `${repo} is private again`)
    } catch (err) {
      toast.error((err as Error).message)
    }
  }

  return (
    <div class="flex flex-col gap-3">
      <p class="text-sm text-muted-foreground">
        Anyone who can reach this server can search and read the indexes checked here,
        without logging in. Everything else stays behind the login.
      </p>

      <div class="flex flex-col gap-0.5">
        <For
          each={reposQuery.data ?? []}
          fallback={<div class="text-sm text-muted-foreground">no indexes</div>}
        >
          {repo => (
            <label class="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm transition-colors hover:bg-accent/50">
              <div
                class={isPublic(repo)
                  ? 'i-lucide-check-square size-4 shrink-0'
                  : 'i-lucide-square size-4 shrink-0 text-muted-foreground'}
                onClick={() => void toggle(repo, !isPublic(repo))}
              />
              <span class="min-w-0 flex-1 truncate font-mono">{repo}</span>
              <Show when={isPublic(repo)}>
                <span class="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">guest</span>
              </Show>
            </label>
          )}
        </For>
      </div>

      <Show when={stale().length}>
        <div class="border-t border-border pt-2">
          <div class="mb-1 text-xs text-muted-foreground">
            granted, but no such index right now
          </div>
          <For each={stale()}>
            {entry => (
              <div class="flex items-center gap-2 px-2 py-1 text-sm">
                <span class="min-w-0 flex-1 truncate text-muted-foreground font-mono">{entry.repo}</span>
                <button
                  type="button"
                  class="shrink-0 text-xs text-destructive hover:underline"
                  onClick={() => void toggle(entry.repo, false)}
                >
                  remove
                </button>
              </div>
            )}
          </For>
        </div>
      </Show>
    </div>
  )
}
